/* eslint-disable */
//
import {
    AiSearchFilter,
    generateCustomTagFilter,
    generateGamificationLevelFilter,
    generateAlignmentTypeFilter,
    generateGrassrootsActionCenterFilter,
    generateIssueAlignmentFilter,
    SearchBarFilter,
    CurrentSearchifySelectionFilter,
    getCustomQuestionField,
} from "shared/search/containers/Filter/Filters"

import { isCustomFilterGroup, isConnectedFilterGroup, getPermissionedFilters } from "shared/search/helperFunctions"

// this entire file is solely for the purpose of creating the store
// we need in order for filters to be registered.

/**
 * Given a filter component, will generate the corresponding object
 * that has all information pertinent to its registration (and then some).
 *
 * @param  {Object} filter:              Function      The filter component that we're pulling data out of.
 * @param  {String} uniqueFilterGroupId: string        The id of the filter group the filter is in.
 * @param  {Number} profileId:           string        Profile ID of the FLV, if applicable.
 * @return {Object}                      an object representing the details of the filter.
 */

export const getFilterInfo = ({ filter, uniqueFilterGroupId, profileId }) => {
    // we must include the deserialization function in the store,
    // because other components (visualizations, safed searches),
    // need to know the deserialized values!
    const defaultDeserializeFunc = filter.defaultProps.filterComponent.defaultProps.deserialize
    const defaultSerializeFunc = filter.defaultProps.filterComponent.defaultProps.serialize
    const defaultSerializationKey = filter.defaultProps.filterComponent.defaultProps.serializationKey

    if (!defaultSerializationKey && !filter.defaultProps.serializationKey) {
        // this happens in the case of multi-filters
        console.error(`Please provide a serialization key for multifilter ${filter.defaultProps.uniqueFilterId}!`)
    }

    const getProfileIdFunc = () => profileId && profileId.toString()

    return {
        computeFilterValueFromSubfilters: filter.defaultProps.computeFilterValueFromProfileId && getProfileIdFunc,
        ...filter.defaultProps,
        serializationKey: filter.defaultProps.serializationKey || defaultSerializationKey,
        deserializeFunc: filter.defaultProps.deserialize || defaultDeserializeFunc,
        serializeFunc: filter.defaultProps.serialize || defaultSerializeFunc,
        uniqueFilterGroupId,
    }
}

/**
 * Filters the custom fields for fields that match the data type
 * and should be displayed in search
 * Then generate the filter based on the custom fields
 * Since date custom field have multiple filters, flattening the filter array
 * @param  {Array} customFields  The organizations's custom fields.
 * @param  {Number} customDataType    WuorumDataType value
 * @return {Array}               An array of custom field filter objects.
 */
export const parseAndGenerateCustomFilters = (customFields, customDataType) =>
    [].concat.apply(
        [],
        customFields
            .filter((field) => field.display_on_filters && field.tag_owner === customDataType)
            .map((field) => generateCustomTagFilter(field)),
    )

/**
 * Filters the action centers for specified gamification levels then generate the filter
 * @param  {Array} actionCentersList  The organization's action centers.
 * @return {Array}               An array of gamification level filter objects.
 */
export const parseAndGenerateGamificationFilters = (actionCentersList) =>
    actionCentersList
        .filter((actionCenter) => Object.keys(actionCenter.gamification_tiers).length)
        .map((actionCenter) => generateGamificationLevelFilter(actionCenter))

/**
 * Generate the relevant AlignmentType filter(s) using the FilterKeyMap
 * @param {Object} alignmentTypes - The organization's AlignmentTypes.
 * @param {Object} filterKeyMap - Mapping of the uniqueFilterId and it's associated filterKey
 * @return {Array} - An array of AlignmentType filter objects.
 */
export const parseAndGenerateAlignmentTypeFilters = ({ alignmentTypes, filterKeyMap }) => {
    return Object.entries(filterKeyMap).map(([uniqueFilterId, filterKey]) =>
        generateAlignmentTypeFilter({
            alignmentTypes,
            uniqueFilterId,
            filterKey,
        }),
    )
}

/**
 * Generate the relevant AlignmentType filter(s) using the FilterKeyMap
 * @param {Object} filterKeyMap - Mapping of the uniqueFilterId and it's associated filterKey
 * @return {Array} - An array of AlignmentType filter objects.
 */
export const parseAndGenerateGrassrootsActionCenterFilters = ({ filterKeyMap }) => {
    return Object.entries(filterKeyMap).map(([uniqueFilterId, filterKey]) =>
        generateGrassrootsActionCenterFilter({
            uniqueFilterId,
            filterKey,
        }),
    )
}

/**
 * Generate the relevant AlignmentType filter(s) using the FilterKeyMap
 * @param {Object} alignmentTypes - The organization's AlignmentTypes.
 * @param {Object} filterKeyMap - Mapping of the uniqueFilterId and it's associated filterKey
 * @return {Array} - An array of AlignmentType filter objects.
 */
export const parseAndGenerateIssueFilters = ({ alignmentTypes, filterKeyMap }) =>
    Object.entries(filterKeyMap).map(([uniqueFilterId, filterKey]) =>
        generateIssueAlignmentFilter({ alignmentTypes, uniqueFilterId, filterKey }),
    )

/**
 * Given a FilterGroup that contains custom fields, generate all the filters
 * within the custom field group and get each filter's filter info.
 * @param  {Object} group        The FilterGroup component.
 * @param  {Array} customFields  The user's custom fields.
 * @param  {Number} profileId    The profile ID of the FLV, if applicable.
 * @return {Array}               An array of filter info objects.
 */
export const getFilterInfoObjsFromCustomFilterGroup = ({ group, customFields, actionCentersList, profileId }) => {
    const { uniqueFilterGroupId } = group.defaultProps
    /*
     * When we concat customField and gamification filters into a single array, and then call getFilterInfo on it,
     * we overwrite the uniqueFilterGroupId of the customField filters with the uniqueFilterGroupId of the gamification filters.
     *
     * We can see this bug manifest in application by setting a custom field filter, but seeing the Grassroots Activity filter
     * group become highlighted.
     * We could modify this function to only call the relevant parseAndGenerate for the group that is passed in as a defaultProp.
     */

    // get the fields that pertain to this data type!
    const customFieldFilters = parseAndGenerateCustomFilters(customFields, group.defaultProps.customDataType)
    if (group.defaultProps.customDataType === DjangIO.app.models.QuorumDataType.grassroots_action.value)
        customFieldFilters.push(getCustomQuestionField(customFields))

    const gamificationLevelFilters = parseAndGenerateGamificationFilters(actionCentersList)

    const additionalFilters = group.defaultProps.additionalFilters || []

    return additionalFilters
        .concat(customFieldFilters)
        .concat(gamificationLevelFilters)
        .map((filter) => getFilterInfo({ filter, uniqueFilterGroupId, profileId }))
}

/**
 * Given a FilterGroup that must be connected to the Redux store, generate all the filters
 * within the group and get each filter's filter info.
 * @param  {Object} group        The FilterGroup component.
 * @param  {Array} alignmentTypes  The user's AlignmentTypes.
 * @return {Array}               An array of filter info objects.
 */
export const getFilterInfoObjsFromConnectedFilterGroup = ({
    group,
    alignmentTypes,
    ledgerSettingsListIds,
    quorumDataType,
}) => {
    const { filterKeyMap, parseFunction, uniqueFilterGroupId } = group.defaultProps

    const filters = parseFunction
        ? parseFunction({
              alignmentTypes,
              ledgerSettingsListIds,
              filterKeyMap,
              quorumDataType,
          })
        : []

    const additionalFilters = group.defaultProps.additionalFilters || []

    return additionalFilters.concat(filters).map((filter) => getFilterInfo({ filter, uniqueFilterGroupId }))
}

/**
 * Given a regular FilterGroup where filters do NOT need to be derived,
 * @param  {Object} group        The FilterGroup component.
 * @param  {Number} profileId    The profile ID of the FLV, if applicable.
 * @return {Array}               An array of filter info objects.
 */
export const getFilterInfoObjsFromRegularFilterGroup = ({ group, profileId }) => {
    const { uniqueFilterGroupId } = group.defaultProps
    const permissionedFilters = getPermissionedFilters(group.defaultProps.filters)

    return permissionedFilters.map((filter) => getFilterInfo({ filter, uniqueFilterGroupId, profileId }))
}

export const combinePersonifiedFilterGroups = (filterGroups, seeMoreFilterGroups) => {
    return filterGroups.concat(seeMoreFilterGroups ? seeMoreFilterGroups : [])
}

/**
 * Given a FilterSet, get the filter info objects of all filters within
 * the filter set. This includes filters that are not explicitly specified
 * within the FilterSet, such as search bar filters and searchify filters
 * if applicable.
 *
 * @param  {Object} filterSet    The FilterSet component.
 * @param  {Array}  customFields Array of user's custom fields.
 * @param  {Number} profileId    The profile ID of the FLV, if applicable.
 * @param  {Bool}   isSearchify  Whether or not search is in Searchify mode.
 * @return {Array}               An array of objects that represent every filter
 *                               contained within the filter set.
 */
export const generateFilterInfoObjs = ({
    filterSet,
    customFields,
    actionCentersList,
    profileId,
    isSearchify,
    alignmentTypes,
    ledgerSettingsListIds,
    hasMultipleBankAccounts,
    quorumDataType,
}) => {
    const { filterGroups, seeMoreFilterGroups } = filterSet.defaultProps

    const combinedFilterGroups = combinePersonifiedFilterGroups(filterGroups, seeMoreFilterGroups)

    const filterDict = combinedFilterGroups
        .map((group) => {
            if (isCustomFilterGroup(group)) {
                return getFilterInfoObjsFromCustomFilterGroup({
                    group,
                    customFields,
                    actionCentersList,
                    profileId,
                })
            } else if (isConnectedFilterGroup(group)) {
                return getFilterInfoObjsFromConnectedFilterGroup({
                    group,
                    alignmentTypes,
                    ledgerSettingsListIds,
                    hasMultipleBankAccounts,
                    quorumDataType,
                })
            } else {
                return getFilterInfoObjsFromRegularFilterGroup({ group, profileId })
            }
        })
        .reduce((acc, filterObjs) => acc.concat(filterObjs), [])

    // register the search bar, since it's not listed in the filterset
    // but it's always there!
    filterDict.push(getFilterInfo({ filter: SearchBarFilter }))
    filterDict.push(getFilterInfo({ filter: AiSearchFilter }))

    // if it's searchify, add a filter that indicates whether or not to show
    // the current searchify selection. This is used in the menu bar of searchify,
    // "Show / Hide Current Selection".
    if (isSearchify) {
        filterDict.push(getFilterInfo({ filter: CurrentSearchifySelectionFilter }))
    }

    return filterDict
}
